/**
 * NamedResourcesTab — Phase 2 of Workspace Overview.
 *
 * Super-admin UI for maintaining the tenant's roster of named people
 * (the actual humans behind the planner's role codes) plus a quick
 * allocation rollup showing how their hours land across active
 * estimates. Phase 3 will replace the "divide-equally" allocation with
 * per-estimate assignments.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Loader2, Plus, Trash2, RefreshCw, Users, UserX, AlertTriangle,
  Mail, Briefcase, Clock, DollarSign, Check, X as XIcon, Edit2,
  UserPlus, Search, Link as LinkIcon,
} from 'lucide-react';
import { useEntityRegionOptions } from '../EntityRegionFilterChips';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FORM = { name: '', email: '', roles: [], weekly_capacity_hours: 40, hourly_rate: '', notes: '', user_id: null, entity_ids: [], regions: [] };
const resRoles = (r) => (r?.roles && r.roles.length ? r.roles : (r?.role ? [r.role] : []));

export default function NamedResourcesTab() {
  const [resources, setResources] = useState([]);
  const [roleLabels, setRoleLabels] = useState({});
  const [allocation, setAllocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // resource id being edited, or 'new'
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [eligibleScope, setEligibleScope] = useState('org');
  const [promoteSearch, setPromoteSearch] = useState('');
  const { entities: erEntities, regions: erRegions } = useEntityRegionOptions();
  // Bulk entity/region assignment
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState({ entity_ids: [], regions: [] });
  const [bulkSaving, setBulkSaving] = useState(false);

  // Region choices narrow to the selected entities' own regions.
  const formRegionOptions = useMemo(() => {
    if (!form.entity_ids?.length) return erRegions;
    const set = new Set();
    erEntities.filter(en => form.entity_ids.includes(en.id)).forEach(en => {
      (en.regions || []).forEach(r => set.add(r));
      if (en.region) set.add(en.region);
    });
    return [...set].sort();
  }, [erEntities, erRegions, form.entity_ids]);

  const bulkRegionOptions = useMemo(() => {
    if (!bulk.entity_ids.length) return erRegions;
    const set = new Set();
    erEntities.filter(en => bulk.entity_ids.includes(en.id)).forEach(en => {
      (en.regions || []).forEach(r => set.add(r));
      if (en.region) set.add(en.region);
    });
    return [...set].sort();
  }, [erEntities, erRegions, bulk.entity_ids]);

  const toggleSelected = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const applyBulk = async () => {
    if (!selected.length) return;
    setBulkSaving(true);
    try {
      await Promise.all(selected.map(id =>
        axios.patch(`${API_URL}/api/named-resources/${id}`,
          { entity_ids: bulk.entity_ids, regions: bulk.regions }, { withCredentials: true })));
      toast.success(`Entity/region assignment applied to ${selected.length} resource${selected.length === 1 ? '' : 's'}`);
      setSelected([]);
      setBulk({ entity_ids: [], regions: [] });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, allocRes] = await Promise.all([
        axios.get(`${API_URL}/api/named-resources`, { withCredentials: true }),
        axios.get(`${API_URL}/api/named-resources/allocation`, { withCredentials: true }).catch(() => null),
      ]);
      setResources(listRes.data?.resources || []);
      setRoleLabels(listRes.data?.role_labels || {});
      if (allocRes) setAllocation(allocRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load named resources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name || '',
      email: r.email || '',
      roles: resRoles(r),
      weekly_capacity_hours: r.weekly_capacity_hours ?? 40,
      hourly_rate: r.hourly_rate ?? '',
      notes: r.notes || '',
      user_id: r.user_id || null,
      entity_ids: r.entity_ids || [],
      regions: r.regions || [],
    });
  };

  const startNew = () => {
    setEditing('new');
    setForm(EMPTY_FORM);
  };

  const startPromote = (u) => {
    setEditing('new');
    setForm({
      ...EMPTY_FORM,
      name: u.name || '',
      email: u.email || '',
      user_id: u.id,
    });
    setPromoteOpen(false);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.roles || form.roles.length === 0) return toast.error('Pick at least one project role');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        roles: form.roles,
        weekly_capacity_hours: Number(form.weekly_capacity_hours) || 40,
        hourly_rate: form.hourly_rate === '' ? null : Number(form.hourly_rate),
        notes: form.notes.trim() || null,
        user_id: form.user_id || null,
        entity_ids: form.entity_ids || [],
        regions: form.regions || [],
      };
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/named-resources`, payload, { withCredentials: true });
        toast.success(form.user_id ? 'User promoted to resource' : 'Resource added');
      } else {
        await axios.patch(`${API_URL}/api/named-resources/${editing}`, payload, { withCredentials: true });
        toast.success('Resource updated');
      }
      cancelEdit();
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (r) => {
    try {
      await axios.patch(`${API_URL}/api/named-resources/${r.id}`, { active: !r.active }, { withCredentials: true });
      toast.success(r.active ? 'Deactivated' : 'Reactivated');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const handleHardDelete = async (r) => {
    if (!window.confirm(`Permanently delete ${r.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/named-resources/${r.id}?hard=true`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const visibleResources = resources.filter(r => showInactive || r.active);
  const activeCount = resources.filter(r => r.active).length;
  const allocLookup = (allocation?.resources || []).reduce((acc, a) => {
    acc[a.id] = a;
    return acc;
  }, {});

  const openPromoteDialog = async () => {
    setPromoteOpen(true);
    setPromoteSearch('');
    setPromoteLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/named-resources/eligible-users`, { withCredentials: true });
      setEligibleUsers(r.data?.users || []);
      setEligibleScope(r.data?.scope || 'org');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load eligible users');
      setPromoteOpen(false);
    } finally {
      setPromoteLoading(false);
    }
  };

  const filteredEligible = eligibleUsers.filter(u => {
    if (!promoteSearch.trim()) return true;
    const q = promoteSearch.trim().toLowerCase();
    return (u.name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.role || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="named-resources-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2040]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="named-resources-tab-root">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-4 h-4 text-[#4ABFCF]" />
                Named-resource roster
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                The actual humans behind your planner role codes. Used by Workspace Overview to surface allocation across active estimates.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAll}
                data-testid="named-resources-refresh"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openPromoteDialog}
                disabled={editing === 'new'}
                data-testid="named-resources-promote-user"
                title="Promote an existing app user to a resource"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                Promote user
              </Button>
              <Button
                size="sm"
                className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
                onClick={startNew}
                disabled={editing === 'new'}
                data-testid="named-resources-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add resource
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <span data-testid="named-resources-count-active">
              <strong className="text-[#0F2040]">{activeCount}</strong> active
            </span>
            <span data-testid="named-resources-count-inactive">
              <strong className="text-[#0F2040]">{resources.length - activeCount}</strong> inactive
            </span>
            <label className="flex items-center gap-1.5 ml-auto cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                data-testid="named-resources-show-inactive"
                className="w-3.5 h-3.5"
              />
              <span>Show inactive</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Edit form (new or existing) */}
      {editing && (
        <Card className="border-[#4ABFCF]/40 bg-[#4ABFCF]/5" data-testid="named-resources-form-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              {editing === 'new' ? 'Add new resource' : `Editing ${form.name || 'resource'}`}
              {form.user_id && (
                <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]" data-testid="named-resources-form-linked-badge">
                  <LinkIcon className="w-2.5 h-2.5 mr-0.5 inline" />
                  Linked to app user
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Alex Smith"
                  data-testid="named-resources-form-name"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="alex@seertech.com"
                  data-testid="named-resources-form-email"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">
                  Project roles * <span className="text-slate-400 font-normal">(select every role this person can fill)</span>
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="named-resources-form-roles">
                  {Object.entries(roleLabels).map(([k, label]) => {
                    const on = form.roles.includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setForm(f => ({
                          ...f,
                          roles: on ? f.roles.filter(x => x !== k) : [...f.roles, k],
                        }))}
                        className={`px-2 py-1 rounded-full text-[11px] border transition-colors ${on ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#4ABFCF]'}`}
                        data-testid={`named-resources-form-role-${k}`}
                        title={label}
                      >
                        {k} · {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">
                  Entity &amp; Region assignment <span className="text-slate-400 font-normal">(who this person works under — used by the Team Availability filters)</span>
                </Label>
                {erEntities.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="named-resources-form-entities">
                    {erEntities.map(en => {
                      const on = (form.entity_ids || []).includes(en.id);
                      return (
                        <button key={en.id} type="button"
                          onClick={() => setForm(f => ({
                            ...f,
                            entity_ids: on ? f.entity_ids.filter(x => x !== en.id) : [...(f.entity_ids || []), en.id],
                          }))}
                          className={`px-2 py-1 rounded-full text-[11px] border transition-colors ${on ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#4ABFCF]'}`}
                          data-testid={`named-resources-form-entity-${en.id}`}
                          title={en.name || en.id}
                        >
                          {en.code || en.id}
                        </button>
                      );
                    })}
                  </div>
                )}
                {formRegionOptions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="named-resources-form-regions">
                    {formRegionOptions.map(rg => {
                      const on = (form.regions || []).includes(rg);
                      return (
                        <button key={rg} type="button"
                          onClick={() => setForm(f => ({
                            ...f,
                            regions: on ? f.regions.filter(x => x !== rg) : [...(f.regions || []), rg],
                          }))}
                          className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${on ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]' : 'bg-white text-slate-500 border-slate-200 hover:border-[#4ABFCF]'}`}
                          data-testid={`named-resources-form-region-${rg.toLowerCase()}`}
                        >
                          {rg}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Weekly capacity (hours)</Label>
                <Input
                  type="number"
                  min={0}
                  max={80}
                  step={0.5}
                  value={form.weekly_capacity_hours}
                  onChange={e => setForm({ ...form, weekly_capacity_hours: e.target.value })}
                  data-testid="named-resources-form-capacity"
                />
              </div>
              <div>
                <Label className="text-xs">Hourly rate (optional)</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={form.hourly_rate}
                  onChange={e => setForm({ ...form, hourly_rate: e.target.value })}
                  placeholder="—"
                  data-testid="named-resources-form-rate"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Notes</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Specialties, availability windows, etc."
                  data-testid="named-resources-form-notes"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving} data-testid="named-resources-form-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
                onClick={handleSave}
                disabled={saving}
                data-testid="named-resources-form-save"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                {editing === 'new' ? 'Add resource' : 'Save changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resource table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Roster</CardTitle>
        </CardHeader>
        <CardContent>
          {selected.length > 0 && (
            <div className="mb-3 rounded-lg border border-[#4ABFCF]/40 bg-[#4ABFCF]/5 p-3" data-testid="named-resources-bulk-bar">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-[#0F2040]">
                  Assign entity &amp; region to {selected.length} selected
                </span>
                <button type="button" onClick={() => setSelected([])}
                  className="text-[10px] text-slate-500 underline hover:text-[#0F2040]"
                  data-testid="named-resources-bulk-clear-selection">clear selection</button>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="named-resources-bulk-entities">
                {erEntities.map(en => {
                  const on = bulk.entity_ids.includes(en.id);
                  return (
                    <button key={en.id} type="button"
                      onClick={() => setBulk(b => ({ ...b, entity_ids: on ? b.entity_ids.filter(x => x !== en.id) : [...b.entity_ids, en.id] }))}
                      className={`px-2 py-1 rounded-full text-[11px] border transition-colors ${on ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#4ABFCF]'}`}
                      data-testid={`named-resources-bulk-entity-${en.id}`}>{en.code || en.id}</button>
                  );
                })}
              </div>
              {bulkRegionOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="named-resources-bulk-regions">
                  {bulkRegionOptions.map(rg => {
                    const on = bulk.regions.includes(rg);
                    return (
                      <button key={rg} type="button"
                        onClick={() => setBulk(b => ({ ...b, regions: on ? b.regions.filter(x => x !== rg) : [...b.regions, rg] }))}
                        className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${on ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]' : 'bg-white text-slate-500 border-slate-200 hover:border-[#4ABFCF]'}`}
                        data-testid={`named-resources-bulk-region-${rg.toLowerCase()}`}>{rg}</button>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-slate-400">Applying replaces each selected person's current entity/region assignment.</span>
                <Button size="sm" className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-7"
                  onClick={applyBulk} disabled={bulkSaving}
                  data-testid="named-resources-bulk-apply">
                  {bulkSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  Apply to {selected.length}
                </Button>
              </div>
            </div>
          )}
          {visibleResources.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-6 text-center" data-testid="named-resources-empty">
              {resources.length === 0
                ? 'No named resources yet — add your first person to start tracking real allocation.'
                : 'No resources match the current filter.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="named-resources-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="text-left font-semibold py-2 pr-2 w-6">
                      <input type="checkbox"
                        checked={visibleResources.length > 0 && selected.length === visibleResources.length}
                        onChange={e => setSelected(e.target.checked ? visibleResources.map(r => r.id) : [])}
                        className="w-3.5 h-3.5 cursor-pointer"
                        data-testid="named-resources-select-all" />
                    </th>
                    <th className="text-left font-semibold py-2 pr-3">Name</th>
                    <th className="text-left font-semibold py-2 pr-3">Role</th>
                    <th className="text-left font-semibold py-2 pr-3">Entity / Region</th>
                    <th className="text-left font-semibold py-2 pr-3">Email</th>
                    <th className="text-right font-semibold py-2 pr-3">Capacity</th>
                    <th className="text-right font-semibold py-2 pr-3">Rate</th>
                    <th className="text-right font-semibold py-2 pr-3">Allocated</th>
                    <th className="text-right font-semibold py-2 pr-3">Weeks</th>
                    <th className="text-right font-semibold py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleResources.map(r => {
                    const alloc = allocLookup[r.id];
                    const weeks = alloc?.weeks_of_work;
                    const overbooked = weeks != null && weeks > 4;
                    return (
                      <tr
                        key={r.id}
                        className={`border-b border-slate-50 ${r.active ? '' : 'opacity-50'}`}
                        data-testid={`named-resources-row-${r.id}`}
                      >
                        <td className="py-2 pr-2">
                          <input type="checkbox" checked={selected.includes(r.id)}
                            onChange={() => toggleSelected(r.id)}
                            className="w-3.5 h-3.5 cursor-pointer"
                            data-testid={`named-resources-select-${r.id}`} />
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-[#0F2040]">{r.name}</span>
                            {r.user_id && (
                              <span
                                className="inline-flex items-center text-[9px] px-1 py-px rounded bg-indigo-100 text-indigo-700 font-semibold"
                                title="Also a logged-in app user"
                                data-testid={`named-resources-user-badge-${r.id}`}
                              >
                                <LinkIcon className="w-2 h-2 mr-0.5" />
                                user
                              </span>
                            )}
                          </div>
                          {r.notes && <div className="text-[10px] text-slate-400 truncate max-w-xs">{r.notes}</div>}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1" data-testid={`named-resource-roles-${r.id}`}>
                            {resRoles(r).map(rc => (
                              <Badge key={rc} variant="secondary" className="text-[10px]" title={roleLabels[rc] || rc}>
                                {rc}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1" data-testid={`named-resource-entity-region-${r.id}`}>
                            {(r.entity_ids || []).map(eid => (
                              <Badge key={eid} className="text-[9px] bg-[#0F2040] text-white border-transparent uppercase">{eid}</Badge>
                            ))}
                            {(r.regions || []).map(rg => (
                              <Badge key={rg} className="text-[9px] bg-[#4ABFCF]/15 text-[#0F2040] border-[#4ABFCF]/40">{rg}</Badge>
                            ))}
                            {!(r.entity_ids || []).length && !(r.regions || []).length && <span className="text-slate-300 text-xs">—</span>}
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-slate-600">
                          {r.email ? (
                            <a href={`mailto:${r.email}`} className="text-[#4ABFCF] hover:underline inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-[180px]">{r.email}</span>
                            </a>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-slate-600">
                          {r.weekly_capacity_hours}h/wk
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-slate-600">
                          {r.hourly_rate != null ? `$${r.hourly_rate}` : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {alloc ? <span>{alloc.allocated_hours}h</span> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {weeks != null ? (
                            <span
                              className={overbooked ? 'text-rose-600 font-semibold' : 'text-slate-600'}
                              title={overbooked ? `Overbooked: more than 4 weeks of work allocated against weekly capacity` : `${weeks} weeks of work at ${r.weekly_capacity_hours}h/wk`}
                            >
                              {weeks}w
                              {overbooked && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEdit(r)}
                              data-testid={`named-resources-edit-${r.id}`}
                              className="h-7 px-2"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(r)}
                              data-testid={`named-resources-toggle-${r.id}`}
                              className="h-7 px-2"
                              title={r.active ? 'Deactivate' : 'Reactivate'}
                            >
                              {r.active ? <UserX className="w-3.5 h-3.5 text-amber-600" /> : <Check className="w-3.5 h-3.5 text-emerald-600" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleHardDelete(r)}
                              data-testid={`named-resources-delete-${r.id}`}
                              className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              title="Permanently delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unfilled roles surfacing — only when actually present. Helps
          admins spot roles with hours but no named owner. */}
      {allocation?.unfilled_roles?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="named-resources-unfilled-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-4 h-4" />
              Unfilled roles ({allocation.unfilled_roles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-amber-700 mb-2">
              These roles have allocated hours on active estimates but no named resources yet. Add at least one person per role to track coverage.
            </p>
            <div className="flex flex-wrap gap-2">
              {allocation.unfilled_roles.map(u => (
                <Badge
                  key={u.role}
                  className="bg-amber-100 text-amber-800 border-amber-300 text-[11px]"
                  data-testid={`named-resources-unfilled-${u.role}`}
                >
                  {u.role} — {u.label} · {u.total_hours.toLocaleString()}h
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Promote-user dialog */}
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent className="max-w-2xl" data-testid="named-resources-promote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#4ABFCF]" />
              Promote a user to resource
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Pick from {eligibleScope === 'global' ? 'every user in the system' : 'users in your organisation'}. Already-promoted users are hidden. After you pick, the form opens pre-filled with their name + email — you just choose the role and capacity.
            </p>
          </DialogHeader>
          {promoteLoading ? (
            <div className="flex items-center justify-center py-10" data-testid="named-resources-promote-loading">
              <Loader2 className="w-5 h-5 animate-spin text-[#0F2040]" />
            </div>
          ) : eligibleUsers.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="named-resources-promote-empty">
              No eligible users in scope — every user has already been promoted, or no users match your RBAC scope.
            </p>
          ) : (
            <>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={promoteSearch}
                  onChange={e => setPromoteSearch(e.target.value)}
                  placeholder="Filter by name, email or role…"
                  className="pl-8"
                  data-testid="named-resources-promote-search"
                />
              </div>
              <div className="max-h-80 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-50" data-testid="named-resources-promote-list">
                {filteredEligible.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-4 text-center">No users match your filter.</p>
                ) : filteredEligible.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => startPromote(u)}
                    data-testid={`named-resources-promote-pick-${u.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#4ABFCF]/5 flex items-center justify-between gap-3 transition"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-[#0F2040] truncate">
                        {u.name || <span className="italic text-slate-400">no name</span>}
                        {u.is_self && (
                          <span className="ml-1.5 text-[9px] uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1 py-px rounded">you</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{u.email || '(no email)'} · {u.role}</div>
                    </div>
                    <Badge variant="secondary" className="text-[10px] flex-shrink-0">Pick</Badge>
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-slate-400 pt-1">
                Showing {filteredEligible.length} of {eligibleUsers.length} eligible · scope: {eligibleScope}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPromoteOpen(false)} data-testid="named-resources-promote-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
